import { Input } from "@/components/ui/input"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import {
  useArticleDetail,
  usePatchArticle,
  useProductGroups,
} from "../../../api/articlesApi"
import { useTemplateSettings } from "../../../api/templateSettingsApi"
import type { ArticleOut, ArticleUpdateIn } from "../../../api/types/masterData"
import { intlLocale } from "../../../utils/date"
import { formatUnit } from "../../../utils/format"
import { AdditionalAttributes } from "../../shared/AdditionalAttributes"
import { CollapsibleSection } from "../../shared/CollapsibleSection"
import { EditActionBar } from "../../shared/EditActionBar"
import { FieldsGrid } from "../../shared/FieldsGrid"
import { FieldsSectionSkeleton } from "../../shared/FieldsSectionSkeleton"
import { McPill } from "../../shared/McPill"
import { EntityFieldsHeader } from "../EntityFieldsHeader"
import {
  useEntityFieldsState,
  type EntityFieldsState,
} from "../useEntityFieldsState"
import { useScrollIntoViewOnEdit } from "../useScrollIntoViewOnEdit"
import { ArticleProductGroupCombobox } from "./ArticleProductGroupCombobox"

interface ArticleFieldsState
  extends Omit<EntityFieldsState<ArticleOut, ArticleUpdateIn>, "entity"> {
  article: ArticleOut | undefined
}

export function useArticleFieldsState(): ArticleFieldsState {
  const { entity: article, ...rest } = useEntityFieldsState(
    useArticleDetail,
    usePatchArticle,
  )
  return { article, ...rest }
}

interface SectionProps {
  state: ArticleFieldsState
}

export function ArticleFieldsHeader({ state }: SectionProps) {
  return (
    <EntityFieldsHeader
      entity={state.article}
      isLoading={state.isLoading}
      editing={state.editing}
      setEditing={state.setEditing}
      labelKey="MC_MD_TAB_ARTICLES"
      name={state.article?.name}
      subtitle={state.article?.articleNumber}
    />
  )
}

export function ArticleFieldsBody({ state }: SectionProps) {
  const { translate, currentLanguage } = useTranslation()
  const { data: templateSettings } = useTemplateSettings()
  const { data: productGroups } = useProductGroups()
  const productGroupOptions = productGroups ?? []
  const productGroupLabel =
    templateSettings?.productGroupLabel ||
    translate("MC_MD_FIELD_PRODUCT_GROUP")
  const {
    article,
    isLoading,
    editing,
    edits,
    setEdits,
    isPending,
    handleSave,
    handleCancel,
  } = state
  const [fieldsOpen, setFieldsOpen] = useState(false)
  const fieldsRef = useScrollIntoViewOnEdit<HTMLDivElement>(editing)

  if (isLoading) return <FieldsSectionSkeleton count={5} />
  if (!article) return null

  return (
    <div ref={fieldsRef} className="flex flex-col">
      <CollapsibleSection
        label={translate("MC_MD_FIELDS_SECTION")}
        open={editing || fieldsOpen}
        onOpenChange={setFieldsOpen}
      >
        <FieldsGrid
          fields={[
            {
              label: translate("MC_MD_PANEL_COL_ARTICLE"),
              value: <span className="font-mono">{article.articleNumber}</span>,
            },
            {
              label: translate("MC_MD_PANEL_COL_DESCRIPTION"),
              value: editing ? (
                <Input
                  value={edits.name ?? article.name}
                  onChange={(e) =>
                    setEdits((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                article.name
              ),
            },

            {
              label: translate("MC_MD_FIELD_MATERIAL_GROUP"),
              value: editing ? (
                <Input
                  value={edits.materialGroup ?? article.materialGroup ?? ""}
                  onChange={(e) =>
                    setEdits((prev) => ({
                      ...prev,
                      materialGroup: e.target.value,
                    }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                (article.materialGroup ?? "—")
              ),
            },
            {
              label: productGroupLabel,
              value: editing ? (
                <ArticleProductGroupCombobox
                  aria-label={productGroupLabel}
                  value={edits.productGroup ?? article.productGroup ?? ""}
                  onChange={(value) =>
                    setEdits((prev) => ({
                      ...prev,
                      productGroup: value,
                    }))
                  }
                  options={productGroupOptions}
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                (article.productGroup ?? "—")
              ),
            },
            {
              label: translate("MC_MD_FIELD_COUNTRY_OF_ORIGIN"),
              value: editing ? (
                <Input
                  value={edits.countryOfOrigin ?? article.countryOfOrigin ?? ""}
                  onChange={(e) =>
                    setEdits((prev) => ({
                      ...prev,
                      countryOfOrigin: e.target.value,
                    }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                (article.countryOfOrigin ?? "—")
              ),
            },
            {
              label: translate("MC_MD_FIELD_MANUFACTURER"),
              value: editing ? (
                <Input
                  value={
                    edits.manufacturerName ?? article.manufacturerName ?? ""
                  }
                  onChange={(e) =>
                    setEdits((prev) => ({
                      ...prev,
                      manufacturerName: e.target.value,
                    }))
                  }
                  className="h-7 w-full border-grey4 bg-grey2 text-xs text-white shadow-none focus-visible:border-yellow1 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              ) : (
                (article.manufacturerName ?? "—")
              ),
            },
            {
              label: translate("MC_MD_FIELD_UNIT"),
              value: (
                <span className="font-mono">
                  {article.unit
                    ? formatUnit(article.unit, currentLanguage)
                    : "—"}
                </span>
              ),
            },
            {
              label: translate("MC_MD_FIELD_WEIGHT_PER_UNIT"),
              value: (
                <span className="font-mono">
                  {article.weightPerUnitKg != null
                    ? `${article.weightPerUnitKg} kg`
                    : "—"}
                </span>
              ),
            },
            {
              label: translate("MC_MD_PANEL_SUBSTANCES_TITLE"),
              value: (
                <span className="font-mono">
                  {article.substancesDeclaredCount}
                </span>
              ),
            },
            {
              label: translate("MC_MD_FIELD_RESTRICTED_SUBSTANCES"),
              value:
                article.restrictedSubstancesCount > 0 ? (
                  <McPill tone="attention">
                    {article.restrictedSubstancesCount}
                  </McPill>
                ) : (
                  <span className="text-grey5">0</span>
                ),
            },
            {
              label: translate("MC_MD_FIELD_LAST_UPDATED"),
              value: (
                <span className="font-mono">
                  {article.updatedAt
                    ? article.updatedAt.toLocaleDateString(
                        intlLocale(currentLanguage),
                      )
                    : "—"}
                </span>
              ),
            },
          ]}
        />
      </CollapsibleSection>

      {article.additionalAttributes && (
        <AdditionalAttributes attributes={article.additionalAttributes} />
      )}

      {editing && (
        <EditActionBar
          onCancel={handleCancel}
          onSave={handleSave}
          isPending={isPending}
        />
      )}
    </div>
  )
}
